import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type DocumentData,
  getFirestore,
} from 'firebase/firestore/lite'
import { getFirebaseApp } from '../lib/firebase'
import type { BlogPost, CoverImageCredit, Publication } from '../types/blog'

type TimestampLike = { toDate: () => Date }

function toISOString(value: unknown): string | null {
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as TimestampLike).toDate === 'function'
  ) {
    return (value as TimestampLike).toDate().toISOString()
  }
  return null
}

function postFromData(data: DocumentData): BlogPost {
  const credit = data.coverImageCredit as Partial<CoverImageCredit> | null | undefined
  return {
    publication: data.publication === 'newsroom' ? 'newsroom' : 'blog',
    slug: String(data.slug ?? ''),
    title: String(data.title ?? ''),
    category: String(data.category ?? 'Insights'),
    excerpt: String(data.excerpt ?? ''),
    content: String(data.content ?? ''),
    coverImage: String(data.coverImage ?? ''),
    coverImageCredit: credit && typeof credit === 'object' ? {
      author: String(credit.author ?? ''),
      authorUrl: String(credit.authorUrl ?? ''),
      sourceUrl: String(credit.sourceUrl ?? ''),
    } : null,
    author: String(data.author ?? 'Bybeit'),
    status: data.status === 'published' ? 'published' : 'draft',
    publishedAt: toISOString(data.publishedAt),
    createdAt: toISOString(data.createdAt),
    updatedAt: toISOString(data.updatedAt),
  }
}

function byPublishedDate(a: BlogPost, b: BlogPost) {
  return (b.publishedAt ?? '').localeCompare(a.publishedAt ?? '')
}

export async function listPublishedPosts(publication: Publication = 'blog'): Promise<BlogPost[]> {
  const db = getFirestore(getFirebaseApp())
  const snapshot = await getDocs(
    query(
      collection(db, 'blogPosts'),
      where('status', '==', 'published'),
    ),
  )

  return snapshot.docs.map(item => postFromData(item.data())).filter(post => post.publication === publication).sort(byPublishedDate)
}

export async function getPublishedPost(slug: string, publication: Publication = 'blog'): Promise<BlogPost | null> {
  try {
    const snapshot = await getDoc(doc(getFirestore(getFirebaseApp()), 'blogPosts', slug))
    if (!snapshot.exists() || snapshot.data().status !== 'published') return null
    const post = postFromData(snapshot.data())
    return post.publication === publication ? post : null
  } catch (error) {
    // Firestore intentionally denies public reads after a story becomes a draft.
    // Treat that transition like a missing public story during client revalidation.
    if (
      error
      && typeof error === 'object'
      && 'code' in error
      && error.code === 'permission-denied'
    ) return null
    throw error
  }
}
